import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ArrowLeft, ArrowRight, Compass, X } from 'lucide-react';
import { Button } from '@/ui';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { useWillkommenOffen } from '@/lib/willkommen';
import { dismissHelferInvite, markTourDone, nextStep, prevStep, startHelferTour, startTour, steps, stopTour, subscribeTour, tourDone, tourState, type TourState } from './state';

/**
 * Knopf „Einführung" für **Helfer** — die kurze Tour im laufenden Turnier.
 *
 * Bewusst ein eigener Knopf: Die große Tour legt ein Demo-Turnier an und
 * beginnt in der Turnierliste, wo „Neues Turnier" steht. Für einen Helfer wäre
 * das der Weg nach draußen, den ihm sonst nichts erlaubt.
 */
export function HelferTourButton({ size = 'md', className }: { size?: 'sm' | 'md'; className?: string }) {
  const t = useT();
  const { id } = useParams();
  if (!id) return null;
  return (
    <Button size={size} className={className} title={t('Kurze Einführung: Ergebnisse eintragen')} onClick={() => startHelferTour(id)}>
      <Compass size={14} /> {t('Einführung')}
    </Button>
  );
}

/** Knopf „Einführung" — startet die Tour (Turnierliste, Seitenleiste) */
export function TourButton({ size = 'md', className }: { size?: 'sm' | 'md'; className?: string }) {
  const [busy, setBusy] = useState(false);
  const t = useT();
  return (
    <Button
      size={size}
      className={className}
      title={t('Kurze Einführung in die App')}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await startTour();
        } finally {
          setBusy(false);
        }
      }}
    >
      <Compass size={14} /> {t('Einführung')}
    </Button>
  );
}

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * „Große Schrift“ vergrößert die Seite über `zoom` auf dem body (docs/19 §3).
 * `getBoundingClientRect()` liefert dann schon vergrößerte Werte, und weil das
 * Overlay im selben gezoomten Kontext liegt, würden sie ein zweites Mal
 * skaliert — der Rahmen läge daneben. Darum hier zurückrechnen.
 */
function zoomFactor(): number {
  const z = Number.parseFloat(getComputedStyle(document.body).zoom || '1');
  return Number.isFinite(z) && z > 0 ? z : 1;
}

/** Overlay mit Spotlight auf `data-tour`-Elemente; wird einmal in App.tsx eingebunden */
export function Tour() {
  const [st, setSt] = useState<TourState>(tourState());
  const [rect, setRect] = useState<Rect | null>(null);
  const [welcome, setWelcome] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const t = useT();
  // neu messen, wenn die Schriftgröße umgeschaltet wird
  const { large } = useTheme();

  useEffect(() => subscribeTour(setSt), []);

  // Erster Besuch: kleine Einladung auf der Turnierliste — nicht auf der
  // Willkommensseite, die unter `#/` davorliegt: dort steht „Einführung / Demo" schon groß
  const willkommen = useWillkommenOffen();
  useEffect(() => {
    if (!st.active && loc.pathname === '/' && !willkommen && !tourDone()) setWelcome(true);
    else setWelcome(false);
  }, [loc.pathname, st.active, willkommen]);

  const alle = steps(st);
  const step = st.active ? alle[st.index] : undefined;
  const route = step && st.tournamentId ? step.route.replace('{t}', st.tournamentId) : undefined;

  // Zur Route des Schritts wechseln
  useEffect(() => {
    if (route && loc.pathname !== route) nav(route);
  }, [route, loc.pathname, nav]);

  // Ziel messen — nach dem Rendern der Seite, mit kurzem Nachfassen, falls es noch nicht da ist
  useLayoutEffect(() => {
    if (!step) return setRect(null);
    let tries = 0;
    let timer = 0;
    const measure = () => {
      // Dasselbe Ziel gibt es oft zweimal (Seitenleiste und Handy-Leiste) — das sichtbare zählt
      const el = step.target ? ([...document.querySelectorAll<HTMLElement>(`[data-tour="${step.target}"]`)].find((x) => x.getBoundingClientRect().width > 0) ?? null) : null;
      if (el) {
        el.scrollIntoView({ block: 'center', inline: 'nearest' });
        const r = el.getBoundingClientRect();
        const z = zoomFactor();
        setRect({ left: r.left / z, top: r.top / z, width: r.width / z, height: r.height / z });
      } else if (tries++ < 20 && step.target) timer = window.setTimeout(measure, 100);
      else setRect(null);
    };
    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [step, loc.pathname, large]);

  // Tastatur
  useEffect(() => {
    if (!st.active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopTour('skipped');
      // Wer in einem Feld tippt oder einen Knopf mit Enter auslöst, meint nicht die Tour
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      if (e.key === 'Enter' && (tag === 'BUTTON' || tag === 'A')) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') nextStep();
      if (e.key === 'ArrowLeft') prevStep();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [st.active]);

  // Einladung: für die Leitung beim ersten Besuch der Turnierliste, für Helfer beim ersten Öffnen des Turniers — je einmal
  const helferEinladung = !st.active ? st.einladung : null;
  if (welcome || helferEinladung) {
    const helferModus = !!helferEinladung;
    return (
      <div
        className="fixed bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-[90] bg-panel border border-line shadow-[0_30px_80px_-40px_#000] px-4 md:px-5 py-4 w-[calc(100%-1.5rem)] max-w-md flex items-center gap-3 md:gap-4 flex-wrap"
        data-tour-einladung={helferModus ? 'helfer' : 'leitung'}
      >
        <Compass size={22} className="text-red shrink-0" />
        <div className="text-sm flex-1">
          <b>{helferModus ? t('Neu als Helfer?') : t('Neu hier?')}</b>{' '}
          {helferModus
            ? t('Eine Einführung in einer Minute zeigt dir, wie du Spiele startest und Ergebnisse einträgst. Danach fragen wir nicht mehr.')
            : t('Eine Einführung in drei Minuten zeigt dir Teams, Auslosung, Spiele, Tabelle, Beamer, Druck und Cloud — an einem Demo-Turnier.')}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (helferModus) dismissHelferInvite();
            else {
              markTourDone('skipped');
              setWelcome(false);
            }
          }}
        >
          {t('Später')}
        </Button>
        <Button variant="primary" size="sm" onClick={() => (helferModus ? startHelferTour(helferEinladung as string) : void startTour())}>
          {t('Los geht’s')}
        </Button>
      </div>
    );
  }
  if (!step) return null;

  const pad = 6;
  const spot = rect ? { left: rect.left - pad, top: rect.top - pad, width: rect.width + 2 * pad, height: rect.height + 2 * pad } : null;
  // Karte unter dem Ziel, sonst darüber, sonst mittig — Maße im selben (gezoomten) Raum wie das Overlay
  const z = typeof document === 'undefined' ? 1 : zoomFactor();
  const viewW = window.innerWidth / z;
  const viewH = window.innerHeight / z;
  const cardW = 380;
  /**
   * Grob veranschlagte Höhe der Karte. Genau messen müsste man sie erst
   * rendern — für „passt darunter noch?" reicht der Anschlag.
   */
  const cardH = 220;
  let cardStyle: React.CSSProperties = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  if (spot) {
    const left = Math.max(12, Math.min(spot.left, viewW - cardW - 12));
    const unten = spot.top + spot.height + 12;
    const oben = spot.top - 12 - cardH;
    /*
     * Unter das Ziel, wenn es passt, sonst darüber — und wenn das Ziel fast den
     * ganzen Bildschirm einnimmt, notfalls darauf. Vorher konnte die Karte
     * oberhalb des Bildrands landen: Sie war unerreichbar, „Weiter" ließ sich
     * nicht klicken und die Tour saß fest.
     */
    if (unten + cardH < viewH) {
      cardStyle = { left, top: unten, maxHeight: viewH - unten - 12 };
    } else if (oben >= 12) {
      // Von unten her verankert: Ist die Karte höher als veranschlagt, wächst sie nach oben, nicht ins Ziel
      cardStyle = { left, bottom: viewH - spot.top + 12, maxHeight: spot.top - 24 };
    } else {
      /*
       * Ans untere Bildende, nicht auf eine ausgerechnete Oberkante: Mit langem
       * Text war die Karte höher als der Anschlag und „Weiter" lag unter dem
       * Bildrand — die Tour saß fest (Rauchtest, Schritt „Helfer-Link teilen").
       */
      cardStyle = { left, bottom: 12, maxHeight: viewH - 24 };
    }
  }
  const last = st.index === alle.length - 1;

  return (
    <div className="fixed inset-0 z-[100]" data-tour-overlay>
      {spot ? (
        <div className="fixed rounded-md pointer-events-none border-2 border-red" style={{ ...spot, boxShadow: '0 0 0 9999px rgba(0,0,0,0.62)' }} />
      ) : (
        <div className="fixed inset-0 bg-black/60" />
      )}
      <div
        className="fixed bg-panel border border-line shadow-[0_30px_80px_-40px_#000] p-5"
        style={{ width: cardW, maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', ...cardStyle }}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="label mb-1">{t('Einführung · Schritt {i} von {n}', { i: st.index + 1, n: alle.length })}</div>
            <h2 className="m-0 text-[16px] font-bold tracking-tight">{t(step.title)}</h2>
          </div>
          <button className="text-dim hover:text-ink cursor-pointer" onClick={() => stopTour('skipped')} aria-label={t('Einführung beenden')} title={t('Beenden (Esc)')}>
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-muted leading-relaxed mt-2 mb-4">{t(step.text)}</p>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {alle.map((_, i) => (
              <span key={i} className={`inline-block w-1.5 h-1.5 rounded-full ${i === st.index ? 'bg-red' : i < st.index ? 'bg-muted' : 'bg-line'}`} />
            ))}
          </div>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={prevStep} disabled={st.index === 0}>
            <ArrowLeft size={14} /> {t('Zurück')}
          </Button>
          <Button size="sm" variant="primary" onClick={nextStep}>
            {last ? t('Fertig') : t('Weiter')} {!last && <ArrowRight size={14} />}
          </Button>
        </div>
      </div>
    </div>
  );
}
